"use client";

import { useState } from "react";
import { REQUEST_TO_BOOK_BANK_NOTICE } from "../lib/bookingRequestCopy";
import {
  formatResponseDeadline,
  type RequestOutcome,
} from "../lib/requestStatus";

export interface BookingRequestOutcomeProps {
  outcome: RequestOutcome;
  /** ISO deadline for the fleet's answer. */
  expiresAt?: string | null;
  declineReason?: string | null;
  /** Renter-safe explanation of a failed request, when the server gave one. */
  failureReason?: string | null;
  /** IANA zone to show the deadline in; defaults to the viewer's own. */
  timeZone?: string;
  /** Withdraws the request. Omit to hide the cancel control. */
  onCancel?: () => Promise<void>;
  className?: string;
}

type CancelState = "idle" | "confirming" | "cancelling" | "cancelled";

/**
 * Terminal screen of a Request to Book checkout: sent (awaiting the fleet),
 * declined or failed, plus the cancelled state once the renter withdraws.
 * Cancelling is a two-step action and is never triggered on render.
 */
export function BookingRequestOutcome({
  outcome,
  expiresAt,
  declineReason,
  failureReason,
  timeZone,
  onCancel,
  className = "",
}: BookingRequestOutcomeProps) {
  const [cancelState, setCancelState] = useState<CancelState>("idle");
  const [cancelError, setCancelError] = useState<string | null>(null);

  const confirmCancel = async () => {
    if (!onCancel) return;
    setCancelState("cancelling");
    setCancelError(null);
    try {
      await onCancel();
      setCancelState("cancelled");
    } catch (error) {
      setCancelError(
        error instanceof Error && error.message
          ? error.message
          : "We couldn't cancel your request. Please try again.",
      );
      setCancelState("confirming");
    }
  };

  let heading: string;
  let body: React.ReactNode;

  if (cancelState === "cancelled") {
    heading = "Request cancelled";
    body = (
      <p className="text-gray-600">
        Your request was withdrawn and you won&apos;t be charged. Your bank may
        still show a pending charge for a few business days.
      </p>
    );
  } else if (outcome === "declined") {
    heading = "Request declined";
    body = (
      <>
        <p className="text-gray-600">
          The fleet couldn&apos;t accept your request, so you won&apos;t be
          charged. Your bank may show a pending charge for a few business days.
        </p>
        {declineReason && (
          <p className="mt-3 text-gray-700">Reason: {declineReason}</p>
        )}
      </>
    );
  } else if (outcome === "failed") {
    heading = "We couldn't send your request";
    body = (
      <>
        <p className="text-gray-600">
          Something went wrong while sending your request to the fleet, and you
          won&apos;t be charged. Please try again or contact the fleet.
        </p>
        {failureReason && <p className="mt-3 text-gray-700">{failureReason}</p>}
      </>
    );
  } else {
    const deadline = formatResponseDeadline(expiresAt, timeZone);
    heading = "Request sent";
    body = (
      <>
        <p className="text-gray-600">
          The fleet will review your request and email you their answer.
          {deadline ? ` They will respond by ${deadline}.` : ""}
        </p>
        <p className="mt-3 text-sm text-gray-600">
          If they approve, your booking is confirmed and your card is charged.
          If they decline, or don&apos;t respond in time, the request expires.
        </p>
        <p className="mt-3 text-sm text-gray-600">
          {REQUEST_TO_BOOK_BANK_NOTICE}
        </p>
      </>
    );
  }

  const canCancel =
    outcome === "awaiting_approval" &&
    cancelState !== "cancelled" &&
    !!onCancel;

  return (
    <div
      className={`flex items-center justify-center min-h-[500px] ${className}`}
    >
      <div className="text-center bg-white rounded-xl border border-gray-200 p-8 sm:p-12 max-w-lg">
        <h2 className="text-2xl font-semibold text-gray-900 mb-3">{heading}</h2>
        {body}

        {canCancel && cancelState === "idle" && (
          <button
            type="button"
            onClick={() => setCancelState("confirming")}
            className="mt-6 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel request
          </button>
        )}

        {canCancel && cancelState !== "idle" && (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-gray-700">
              Cancel this request? The fleet won&apos;t be able to approve it.
            </p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={confirmCancel}
                disabled={cancelState === "cancelling"}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelState === "cancelling"
                  ? "Cancelling..."
                  : "Yes, cancel request"}
              </button>
              <button
                type="button"
                onClick={() => setCancelState("idle")}
                disabled={cancelState === "cancelling"}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Keep request
              </button>
            </div>
          </div>
        )}

        {cancelError && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {cancelError}
          </p>
        )}
      </div>
    </div>
  );
}
